import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Link,
} from '@mui/material';

export const TermsPage: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center">
          End User License Agreement (EULA)
        </Typography>

        <Typography variant="subtitle1" color="text.secondary" align="center" paragraph>
          Last Updated: October 2, 2026
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            1. Grant of License
          </Typography>
          <Typography paragraph>
            Subject to your compliance with this Agreement, NinaSoftware grants you a limited, non-exclusive,
            non-transferable, revocable license to install and use the Software (EmotionCap, Face Mocap,
            and Pose Motion Capture tools) solely for your internal business purposes or personal use.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            2. Scope of License
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
            2.1 Permitted Uses
          </Typography>
          <Typography paragraph>
            You may use the Software as a tool to create, develop, and produce original content including:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <li><Typography>Commercial video games and interactive experiences</Typography></li>
            <li><Typography>Films, animations, and cinematics for commercial or non-commercial purposes</Typography></li>
            <li><Typography>Virtual reality (VR) and augmented reality (AR) content</Typography></li>
            <li><Typography>Broadcast content, advertisements, and marketing materials</Typography></li>
            <li><Typography>Educational and training materials</Typography></li>
            <li><Typography>Live performances and real-time productions</Typography></li>
          </Box>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
            2.2 Output Content Rights
          </Typography>
          <Typography paragraph>
            You own all rights, title, and interest in and to the content you create using the Software
            ("Output Content"), including motion capture data, facial animation data, and derived animations.
            You may freely distribute, license, sell, or otherwise commercially exploit your Output Content
            without additional fees or royalties to NinaSoftware.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            3. Restrictions and Prohibited Uses
          </Typography>
          <Typography paragraph>
            You expressly agree that you will NOT:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <li>
              <Typography>
                <strong>Reverse engineer, decompile, or disassemble</strong> the Software or attempt to
                derive the source code, underlying ideas, algorithms, or structure of the Software
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Modify, adapt, translate, or create derivative works</strong> of the Software
                or any portion thereof
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Distribute, sublicense, rent, lease, or lend</strong> the Software or any copies
                thereof to any third party
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Remove, alter, or obscure</strong> any proprietary notices, labels, or marks on
                or within the Software, including copyright notices, trademarks, and logos
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Use the Software to create competing products</strong> or similar motion capture
                or facial animation software tools
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Incorporate the Software</strong> (in whole or in part) into any other software
                product or create a plugin, extension, or wrapper that redistributes the Software
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Share, transfer, or sell your license</strong> or allow multiple users to access
                the Software under a single-user license
              </Typography>
            </li>
            <li>
              <Typography>
                <strong>Use NinaSoftware branding, logos, or trademarks</strong> in connection with your
                Output Content without prior written permission, except for crediting purposes
              </Typography>
            </li>
          </Box>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            4. Intellectual Property Rights
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
            4.1 Software Ownership
          </Typography>
          <Typography paragraph>
            The Software, including all source code, object code, algorithms, user interfaces, design,
            documentation, and any updates or modifications thereto, is and shall remain the exclusive
            property of NinaSoftware. All rights not expressly granted to you under this Agreement are
            reserved by NinaSoftware.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
            4.2 Trademarks
          </Typography>
          <Typography paragraph>
            "NinaSoftware," "EmotionCap," and related logos are trademarks or registered trademarks of
            NinaSoftware. You may not use these marks without prior written consent, except you may include
            a credit such as "Created with NinaSoftware EmotionCap" in your Output Content.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            5. Payments, Credits and Subscriptions
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
            5.1 Subscription Plans
          </Typography>
          <Typography paragraph>
            New accounts receive 100 complimentary promotional credits, with no cash value. These credits cannot be refunded or exchanged for money. The service also offers prepaid credit packages and monthly subscriptions with included credits.
            Access and usage remain subject to the permissions and limits of the selected service or plan.
            A one-time credit purchase does not create a recurring subscription.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
            5.2 Payment and Billing
          </Typography>
          <Typography paragraph>
            Credit purchases and subscription fees are paid in advance. Subscriptions renew monthly unless
            cancelled. Nina Software does not offer voluntary refunds for completed purchases, unused credits
            or unused portions of a paid period. Mandatory consumer rights and applicable payment-provider
            terms remain unaffected. See our <Link href="/refund_policy">Refund &amp; Cancellation Policy</Link>.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
            5.3 Cancellation
          </Typography>
          <Typography paragraph>
            You may cancel your subscription at any time. Cancellation stops future renewals and takes
            effect when the current paid billing period ends. The current month's included benefits remain
            available until that expiration, subject to plan and usage limits. Cancellation does not remove
            already granted credits or create a voluntary prorated refund. Applicable legal rights remain
            unaffected.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
            5.4 Account Security
          </Typography>
          <Typography paragraph>
            You are responsible for maintaining the confidentiality of your account credentials and for all
            activities that occur under your account. You must notify us immediately of any unauthorized use
            of your account.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            6. Usage Restrictions and Compliance
          </Typography>
          <Typography paragraph>
            You agree to use the Software only for lawful purposes and in accordance with this Agreement.
            You will not:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <li>
              <Typography>
                Use the Software to create content that infringes upon the intellectual property rights,
                privacy rights, or other rights of any third party
              </Typography>
            </li>
            <li>
              <Typography>
                Create content that is defamatory, obscene, pornographic, or otherwise objectionable
              </Typography>
            </li>
            <li>
              <Typography>
                Use the Software in any manner that could damage, disable, overburden, or impair our servers
                or networks
              </Typography>
            </li>
            <li>
              <Typography>
                Attempt to gain unauthorized access to any portion of the Software or related systems
              </Typography>
            </li>
            <li>
              <Typography>
                Use the Software in violation of any applicable local, state, national, or international law
              </Typography>
            </li>
          </Box>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            7. Updates and Support
          </Typography>
          <Typography paragraph>
            NinaSoftware may provide updates, upgrades, or patches to the Software at its discretion. Some
            updates may be automatically installed. We may provide technical support as described in your
            subscription plan, but support is not guaranteed and may be modified or discontinued at any time.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            8. Data Collection and Privacy
          </Typography>
          <Typography paragraph>
            The Software may collect certain usage data and analytics to improve functionality and user
            experience. We will handle your personal information in accordance with our <Link href="/privacy_policy">Privacy Policy</Link>.
            You acknowledge that your use of the Software constitutes consent to such data collection
            practices.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            9. Warranty Disclaimer
          </Typography>
          <Typography paragraph>
            THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, EITHER EXPRESS OR IMPLIED,
            INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            OR NON-INFRINGEMENT. NinaSoftware does not warrant that the Software will be error-free,
            uninterrupted, secure, or that defects will be corrected.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            10. Limitation of Liability
          </Typography>
          <Typography paragraph>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL NinaSoftware BE LIABLE
            FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF
            PROFITS, REVENUE, DATA, OR USE, WHETHER IN AN ACTION IN CONTRACT, TORT, OR OTHERWISE, ARISING
            OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THIS AGREEMENT, EVEN IF ADVISED OF THE POSSIBILITY
            OF SUCH DAMAGES.
          </Typography>
          <Typography paragraph>
            NinaSoftware's total liability to you for all claims arising from or relating to this Agreement
            or the Software shall not exceed the amount you paid to NinaSoftware in the twelve (12) months
            preceding the claim.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            11. Termination
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
            11.1 Termination by You
          </Typography>
          <Typography paragraph>
            You may terminate this Agreement at any time by canceling your subscription and ceasing all use
            of the Software.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
            11.2 Termination by NinaSoftware
          </Typography>
          <Typography paragraph>
            NinaSoftware may suspend or terminate your access to the Software immediately, without prior
            notice, if you breach any provision of this Agreement. We may also terminate or suspend your
            access for any reason with 30 days' notice.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom sx={{ mt: 2 }}>
            11.3 Effect of Termination
          </Typography>
          <Typography paragraph>
            Upon termination, you must immediately cease all use of the Software and delete or destroy all
            copies in your possession or control. Sections relating to intellectual property, limitations
            of liability, and other provisions that by their nature should survive termination will continue
            in effect.
          </Typography>
          <Typography paragraph>
            Your Output Content created prior to termination remains your property and is unaffected by
            termination of this Agreement.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            12. Export Control
          </Typography>
          <Typography paragraph>
            The Software may be subject to export control laws and regulations. You agree to comply with all
            applicable export and import laws and regulations and will not export or re-export the Software
            in violation of such laws.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            13. Governing Law and Dispute Resolution
          </Typography>
          <Typography paragraph>
            This Agreement shall be governed by and construed in accordance with the laws of [Your Jurisdiction],
            without regard to its conflict of law principles. Any dispute arising from this Agreement shall be
            resolved through binding arbitration in accordance with the rules of [Arbitration Organization].
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            14. Changes to This Agreement
          </Typography>
          <Typography paragraph>
            NinaSoftware reserves the right to modify this Agreement at any time. We will provide notice of
            material changes by email or through the Software. Your continued use of the Software after such
            notice constitutes acceptance of the modified terms.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            15. Entire Agreement
          </Typography>
          <Typography paragraph>
            This Agreement constitutes the entire agreement between you and NinaSoftware regarding the Software
            and supersedes all prior agreements, understandings, and communications, whether written or oral.
          </Typography>

          <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4 }}>
            16. Contact Information
          </Typography>
          <Typography paragraph>
            For questions about this Agreement or the Software, please contact:
            <br />
            <strong>NinaSoftware</strong>
            <br />
            <strong>Email:</strong> comercial@ninasoftware.com
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="body2" color="text.secondary" align="center">
            BY INSTALLING, COPYING, OR USING THE SOFTWARE, YOU ACKNOWLEDGE THAT YOU HAVE READ THIS AGREEMENT,
            UNDERSTAND IT, AND AGREE TO BE BOUND BY ITS TERMS AND CONDITIONS.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};
